// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Row,
  Card,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  CardBody,
  CardFooter,
  CardSubtitle,
  CardImg,
  Label,
  FormGroup,
  CardText,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink
} from "reactstrap";
import { NavLink } from "react-router-dom";
import Select from "react-select";
import CustomSelect from "Components/CustomSelect";

import IntlMessages from "Util/IntlMessages";
import { Colxx, Separator } from "Components/CustomBootstrap";
import { BreadcrumbItems } from "Components/BreadcrumbContainer";

import mouseTrap from "react-mousetrap";
import axios from "axios";
import apiProductsLocal from "Data/apiProducts.json";

const apiUrl = "https://www.dotjs.net/apiproducts.json";

const PRODUCT_STATUSES = [
  { label: "Processed", value: "PROCESSED", statusColor: "secondary" },
  { label: "On hold", value: "ON HOLD", statusColor: "primary" }
];

/** Prefer bundled Unsplash URLs per id when the API still returns legacy /assets/ paths. */
const productDemoImgById = Object.fromEntries(
  (apiProductsLocal.data || []).map(p => [String(p.id), p.img])
);

function mergeProductDemoImages(items) {
  if (!Array.isArray(items)) return [];
  return items.map(p => ({
    ...p,
    img: productDemoImgById[String(p.id)] || p.img
  }));
}

const initialCategories = [
  { label: "Cakes", value: "Cakes", key: 0 },
  { label: "Desserts", value: "Desserts", key: 1 },
  { label: "Cupcakes", value: "Cupcakes", key: 2 },
  { label: "Other", value: "Other", key: 3 }
];

function ProductsPageLayout() {
  const [categories] = useState(initialCategories);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState();

  const toggleModal = useCallback(() => {
    setModalOpen(o => !o);
  }, []);

  const openEditModal = useCallback(product => {
    setEditModalOpen(true);
    setEditDraft({ ...product });
  }, []);

  const toggleEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditDraft(null);
  }, []);

  const openDeleteModal = useCallback(product => {
    setDeleteModalOpen(true);
    setDeleteTarget(product);
  }, []);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  }, []);

  const updateEditDraft = useCallback(patch => {
    setEditDraft(d => (d ? { ...d, ...patch } : null));
  }, []);

  const saveEdit = useCallback(() => {
    if (!editDraft) return;
    const d = editDraft;
    const statusMeta = PRODUCT_STATUSES.find(x => x.value === d.status) || {
      statusColor: d.statusColor || "secondary"
    };
    setItems(prev =>
      prev.map(p =>
        p.id === d.id
          ? {
              ...p,
              title: d.title,
              description: d.description,
              category: d.category,
              status: d.status,
              statusColor: statusMeta.statusColor || p.statusColor
            }
          : p
      )
    );
    setEditModalOpen(false);
    setEditDraft(null);
  }, [editDraft]);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    const t = deleteTarget;
    setItems(prev => prev.filter(p => p.id !== t.id));
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  }, [deleteTarget]);

  useEffect(() => {
    axios
      .get(`${apiUrl}?`, { timeout: 12000 })
      .then(res => res.data)
      .then(data => {
        setItems(
          mergeProductDemoImages(Array.isArray(data.data) ? data.data : [])
        );
        setIsLoading(true);
      })
      .catch(() => {
        setItems(apiProductsLocal.data || []);
        setIsLoading(true);
      });
  }, []);

  function renderEditModal() {
    if (!editDraft) return null;

    const categoryValue = categories.find(c => c.value === editDraft.category);
    const statusValue = PRODUCT_STATUSES.find(s => s.value === editDraft.status);

    return (
      <Modal
        isOpen={editModalOpen}
        toggle={toggleEditModal}
        wrapClassName="modal-right"
        backdrop="static"
      >
        <ModalHeader toggle={toggleEditModal}>
          <IntlMessages id="products.edit-item" />
        </ModalHeader>
        <ModalBody>
          <Label>
            <IntlMessages id="product-name" />
          </Label>
          <Input
            value={editDraft.title}
            onChange={e => updateEditDraft({ title: e.target.value })}
          />
          <Label className="mt-4">
            <IntlMessages id="product.category" />
          </Label>
          <Select
            key={`cat-${editDraft.id}`}
            components={{ Input: CustomSelect }}
            className="react-select"
            classNamePrefix="react-select"
            value={categoryValue || null}
            onChange={opt =>
              updateEditDraft({ category: opt ? opt.value : "" })
            }
            options={categories}
          />
          <Label className="mt-4">
            <IntlMessages id="product.description" />
          </Label>
          <Input
            type="textarea"
            value={editDraft.description}
            onChange={e =>
              updateEditDraft({ description: e.target.value })
            }
          />
          <Label className="mt-4">
            <IntlMessages id="product.status" />
          </Label>
          <Select
            key={`st-${editDraft.id}`}
            components={{ Input: CustomSelect }}
            className="react-select"
            classNamePrefix="react-select"
            value={statusValue || null}
            onChange={opt =>
              opt &&
              updateEditDraft({
                status: opt.value,
                statusColor: opt.statusColor
              })
            }
            options={PRODUCT_STATUSES}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={toggleEditModal}>
            <IntlMessages id="product.cancel" />
          </Button>
          <Button color="primary" onClick={saveEdit}>
            <IntlMessages id="products.save-changes" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  function renderDeleteModal() {
    return (
      <Modal
        isOpen={deleteModalOpen}
        toggle={toggleDeleteModal}
        centered
        backdrop="static"
      >
        <ModalHeader toggle={toggleDeleteModal}>
          <IntlMessages id="products.delete-confirm-title" />
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            <IntlMessages id="products.delete-confirm-body" />
          </p>
          {deleteTarget ? (
            <p className="mt-3 mb-0 font-weight-medium">{deleteTarget.title}</p>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={toggleDeleteModal}>
            <IntlMessages id="product.cancel" />
          </Button>
          <Button color="danger" onClick={confirmDelete}>
            <IntlMessages id="products.delete-confirm" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return !isLoading ? (
    <div className="loading" />
  ) : (
    <Fragment>
      <div className="disable-text-selection products-page">
        <Row>
          <Colxx xxs="12">
            <div className="products-page__header mb-2 d-flex flex-column flex-md-row flex-md-wrap align-items-md-center gap-3">
              <div className="flex-grow-1">
                <h1 className="mb-1">
                  <IntlMessages id="menu.products.page" />
                </h1>
                <BreadcrumbItems />
              </div>
              <div className="d-flex flex-shrink-0">
                <Button color="primary" size="lg" onClick={toggleModal}>
                  <IntlMessages id="products.add-new" />
                </Button>
              </div>
            </div>

            <Separator className="mb-4" />

            <Modal
              isOpen={modalOpen}
              toggle={toggleModal}
              wrapClassName="modal-right"
              backdrop="static"
            >
              <ModalHeader toggle={toggleModal}>
                <IntlMessages id="products.add-new-items" />
              </ModalHeader>
              <ModalBody>
                <Label>
                  <IntlMessages id="product-name" />
                </Label>
                <Input />
                <Label className="mt-4">
                  <IntlMessages id="product.category" />
                </Label>
                <Select
                  components={{ Input: CustomSelect }}
                  className="react-select"
                  classNamePrefix="react-select"
                  name="form-field-name"
                  options={categories}
                />
                <Label className="mt-4">
                  <IntlMessages id="product.description" />
                </Label>
                <Input type="textarea" name="text" id="exampleText" />
                <Label className="mt-4">
                  <IntlMessages id="product.status" />
                </Label>
                <FormGroup check>
                  <Input
                    type="radio"
                    id="exCustomRadio"
                    name="customRadio"
                  />
                  <Label for="exCustomRadio" check>
                    IN STOCK
                  </Label>
                </FormGroup>
                <FormGroup check>
                  <Input
                    type="radio"
                    id="exCustomRadio2"
                    name="customRadio"
                  />
                  <Label for="exCustomRadio2" check>
                    OUT OF STOCK
                  </Label>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="secondary"
                  outline
                  onClick={toggleModal}
                >
                  <IntlMessages id="product.cancel" />
                </Button>
                <Button color="primary" onClick={toggleModal}>
                  <IntlMessages id="product.submit" />
                </Button>{" "}
              </ModalFooter>
            </Modal>

            {renderEditModal()}
            {renderDeleteModal()}
          </Colxx>
        </Row>
        <Row>
          {items.map(product => {
            return (
              <Colxx sm="6" lg="4" xl="3" className="mb-4" key={product.id}>
                <Card className="product-tile h-100">
                  <div className="product-tile__media position-relative">
                    <NavLink
                      to={`?p=${product.id}`}
                      className="product-tile__media-link d-block"
                    >
                      <CardImg
                        className="product-tile__img"
                        top
                        alt={product.title}
                        src={product.img}
                      />
                    </NavLink>
                    <Badge
                      color={product.statusColor}
                      pill
                      className="product-tile__badge zero-radius"
                    >
                      {product.status}
                    </Badge>
                  </div>
                  <CardBody className="d-flex flex-column">
                    <CardSubtitle className="mb-2">{product.title}</CardSubtitle>
                    <p className="text-muted text-small mb-2 font-weight-light flex-grow-1">
                      {product.description}
                    </p>
                    <p className="text-extra-small text-muted mb-3">
                      {product.category}
                    </p>
                    <div className="d-flex flex-wrap gap-2 mt-auto">
                      <Button
                        size="sm"
                        color="primary"
                        type="button"
                        onClick={() => openEditModal(product)}
                      >
                        {product.edit || "Edit"}
                      </Button>
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        type="button"
                        onClick={() => openDeleteModal(product)}
                      >
                        {product.delete || "Delete"}
                      </Button>
                    </div>
                  </CardBody>
                  <CardFooter className="text-center text-small">
                    <span className="fa fa-shopping-basket me-1" />
                    <span>{product.sales}</span>
                    <span className="fa fa-university ms-3 me-1" />
                    <span>{product.stock}</span>
                  </CardFooter>
                </Card>
              </Colxx>
            );
          })}
        </Row>
        <Colxx lg="8" className="mx-auto mt-4 mb-5">
          <Pagination size="sm" aria-label="Page navigation example">
            <PaginationItem>
              <PaginationLink className="fa fa-angle-left" href="#" />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink className="fa fa-angle-double-left" href="#" />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#">1</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#">2</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#">3</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#">4</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#">5</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink className="fa fa-angle-right" href="#" />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink className="fa fa-angle-double-right" href="#" />
            </PaginationItem>
          </Pagination>
        </Colxx>
      </div>
    </Fragment>
  );
}

export default mouseTrap(ProductsPageLayout);
